/**
 * `makeStyles` — a themed `StyleSheet.create`.
 *
 *   const useStyles = makeStyles((t) => ({
 *     card: { ...t.material.frostCard, padding: 16 },
 *     title: { color: t.ink.primary, fontSize: 17 },
 *   }));
 *
 *   function Card() {
 *     const styles = useStyles();
 *     return <View style={styles.card}>…</View>;
 *   }
 *
 * The factory runs ONCE PER PALETTE for the life of the app, on first use, and the
 * resulting sheet is cached — so a component gets the same object on every render
 * and a theme switch costs one `StyleSheet.create` per module, not one per render.
 *
 * Write the factory as a pure function of `t`. It is not a hook: no state, no
 * props. A style that depends on a prop stays an inline override at the call site,
 * exactly as with a static sheet.
 */
import { StyleSheet, type ImageStyle, type TextStyle, type ViewStyle } from 'react-native';
import { useTheme } from './ThemeContext';
import type { Palette } from './palette';

type NamedStyles<T> = { [K in keyof T]: ViewStyle | TextStyle | ImageStyle };

export function makeStyles<T extends NamedStyles<T>>(factory: (t: Palette) => T): () => T {
  // Keyed on the palette object, not the scheme name: the two palettes are module
  // constants, so identity is exact, and a WeakMap cannot hold a stale sheet for a
  // palette that no longer exists.
  const cache = new WeakMap<Palette, T>();
  return function useStyles(): T {
    const { palette } = useTheme();
    let sheet = cache.get(palette);
    if (!sheet) {
      sheet = StyleSheet.create(factory(palette)) as T;
      cache.set(palette, sheet);
    }
    return sheet;
  };
}
